/**
 * @module cercaliagl/view/VIsochrones
 */

import $ from 'jquery';
import { MDCSlider } from '@material/slider';
import { MDCLinearProgress } from '@material/linear-progress';
import BaseView from './BaseView';
import { i18n } from '../dictionary';
import {
  createRadiobox, createSlider, createProgressBar, createCheckbox,
} from './VUtils';

/**
 * @typedef {Object} IsochronesOptions
 * @property {string} weight Weight. `distance|time`
 * @property {number} isolevels Iso levels
 * @property {string} method Method `convexhull|concavehull`
 * @property {number} inverse Inverse `0|1`
 * @property {boolean} increment false | true
 * @property {string} net `car|bike|foot`
 */

/**
 * @enum {string}
 */
const Method = {
  CONVEXHULL: 'convexhull',
  CONCAVEHULL: 'concavehull',
};

/**
 * @enum {string}
 */
const Weight = {
  DISTANCE: 'distance',
  TIME: 'time',
};

/**
 * @enum {string}
 */
const Net = {
  CAR: 'car',
  BIKE: 'bike',
  FOOT: 'foot',
};

/**
 * @enum {any}
 */
const DefaultOptions = {
  WEIGHT: Weight.DISTANCE,
  METHOD: Method.CONCAVEHULL,
  ISOLEVELS_DIST: 10 * 1000, // meters
  ISOLEVELS_TIME: 30 * 60 * 1000, // ms
  INVERSE: 0,
};

/**
 * @enum {string}
 */
export const EventType = {
  SUBMIT: 'submit',
  SAVE_KML: 'savekml',
  REMOVE: 'remove',
  CLOSE: 'close',
};

/**
 * @enum {string}
 */
export const DistanceMaxValue = {
  CAR: 60,
  BIKE: 50,
  FOOT: 40,
};

/**
 * @classdesc
 */
class VIsochrones extends BaseView {
  /**
   * @constructor
   */
  constructor() {
    const jqElement = $(`
      <div class="v-isochrones mdc-elevation--z4">
        <div class="content">
          ${createProgressBar()}      
          <div class="title">${i18n('calculateIsochrone')}</div>
          <div class="row">
            <div class="column">${createRadiobox('net', Net.CAR, true, i18n('car'), true)}</div>
            <div class="column">${createRadiobox('net', Net.BIKE, true, i18n('bicycle'), false)}</div>
            <div class="column">${createRadiobox('net', Net.FOOT, true, i18n('onFoot'), false)}</div>
          </div>
          <div id="methodRow" class="row">
            <div class="column">${createRadiobox('method', Method.CONVEXHULL, true, i18n('simplified'), false)}</div>
            <div class="column">${createRadiobox('method', Method.CONCAVEHULL, true, i18n('detailed'), true)}</div>
          </div>
          <div>
            <div class="btns">
              <button type="button" class="mdc-icon-button weight active" name="${Weight.DISTANCE}">
                <span class="mdc-icon-button__icon icon-distance"></span>
              </button>
              <button type="button" class="mdc-icon-button weight" name="${Weight.TIME}">
                <span class="mdc-icon-button__icon icon-time"></span>
              </button>
            </div>
            <div class="sliders">
              <div class="distcar">
                ${createSlider('distslidercar', 5, DistanceMaxValue.CAR, DefaultOptions.ISOLEVELS_DIST / 1000, true, 'km')}
              </div>
              <div class="distbicycle">
                ${createSlider('distsliderbicycle', 5, DistanceMaxValue.BIKE, DefaultOptions.ISOLEVELS_DIST / 1000, true, 'km')}
              </div>
              <div class="distonfoot">
                ${createSlider('distslideronfoot', 5, DistanceMaxValue.FOOT, DefaultOptions.ISOLEVELS_DIST / 1000, true, 'km')}
              </div>
              <div class="time">
                ${createSlider('timeslider', 1, 50, DefaultOptions.ISOLEVELS_TIME / (60 * 1000), true, 'min')}
              </div>
            </div>
          </div>
          <div class="row">
            <div class="column incrementColumn">${createCheckbox('increment', 'contours_minutes', i18n('increments'), false)}</div>
          </div>
          <div class="row">
            <div class="column">${createRadiobox('inverse', 0, true, i18n('starting'), true)}</div>
            <div class="column">${createRadiobox('inverse', 1, true, i18n('arrival'), false)}</div>
          </div>
        </div>
        <div class="btns-footer">
          <button type="button" class="mdc-icon-button save">
            <span class="mdc-icon-button__icon icon-save"></span>
          </button>
          <button type="button" class="mdc-icon-button remove">
            <span class="mdc-icon-button__icon icon-trash"></span>
          </button>
          <button type="button" class="mdc-icon-button calculate">
            <span class="mdc-icon-button__icon icon-gps"></span>
          </button>
        </div>
      </div>
    `);

    super(jqElement);

    /**
     * @private
     * @type {IsochronesOptions}
     */
    this.isochronesOptions_ = {
      weight: Weight.DISTANCE,
      method: Method.CONCAVEHULL,
      isolevels: DefaultOptions.ISOLEVELS_DIST,
      inverse: 0,
      increment: false,
      net: Net.CAR,
    };

    /**
     * @private
     * @type {MDCSlider}
     */
    this.mdcTimeSlider_ = null;

    /**
     * @private
     * @type {MDCSlider}
     */
    this.mdcDistSliderCar_ = null;

    this.initialize_();
  }

  /**
   * @override
   */
  show() {
    this.jqElement_.show();
    this.refreshSlidersLayout_();
  }

  /**
   * Necessary after create and after show/hide
   * @private
   */
  refreshSlidersLayout_() {
    this.mdcDistSliderCar_.layout();
    this.mdcDistSliderBicycle_.layout();
    this.mdcDistSliderOnFoot_.layout();
    this.mdcTimeSlider_.layout();
  }

  /**
   * @private
   */
  initialize_() {
    if (!this.jqElement_) {
      return;
    }
    const jqDistsSliderCar = this.jqElement_.find('.distslidercar');
    const jqDistsSliderBicycle = this.jqElement_.find('.distsliderbicycle');
    const jqDistsSliderOnFoot = this.jqElement_.find('.distslideronfoot');
    const jqTimesSlider = this.jqElement_.find('.timeslider');
    if (!jqDistsSliderCar || jqDistsSliderCar.length === 0
        || !jqDistsSliderBicycle || jqDistsSliderBicycle.length === 0
        || !jqDistsSliderBicycle || jqDistsSliderBicycle.length === 0
        || !jqTimesSlider || jqTimesSlider.length === 0) {
      return;
    }
    const incrementColumn = this.jqElement_.find('.incrementColumn');
    this.jqElement_.find('button.weight').click((e) => {
      const buttonClicked = e.currentTarget;
      this.jqElement_.find('button.weight').removeClass('active');
      $(buttonClicked).addClass('active');
      this.isochronesOptions_.weight = buttonClicked.name;
      if (buttonClicked.name === Weight.DISTANCE) {
        this.jqElement_.find('.sliders .time').hide();
        incrementColumn.css('display', 'none');
        this.updateDistanceSliderVisibility();
        if (this.isochronesOptions_.net === Net.CAR) {
          this.isochronesOptions_.isolevels = this.mdcDistSliderCar_.getValue() * 1000;
        } else if (this.isochronesOptions_.net === Net.BIKE) {
          this.isochronesOptions_.isolevels = this.mdcDistSliderBicycle_.getValue() * 1000;
        } else {
          this.isochronesOptions_.isolevels = this.mdcDistSliderOnFoot_.getValue() * 1000;
        }
      } else {
        this.jqElement_.find('.sliders .distcar').hide();
        this.jqElement_.find('.sliders .distbicycle').hide();
        this.jqElement_.find('.sliders .distonfoot').hide();
        this.jqElement_.find('.sliders .time').show();
        incrementColumn.css('display', 'flex');
        this.isochronesOptions_.isolevels = this.mdcTimeSlider_.getValue() * 60 * 1000;
      }
      this.refreshSlidersLayout_();
    });

    this.jqElement_.find('input[name=net]').change((e) => {
      this.isochronesOptions_.net = e.target.value;

      if (this.isochronesOptions_.weight === Weight.DISTANCE) {
        this.updateDistanceSliders(this.isochronesOptions_.isolevels / 1000, this.isochronesOptions_.net);
        this.updateDistanceSliderVisibility();
        this.refreshSlidersLayout_();
      }
    });

    this.jqElement_.find('input[name=method]').change((e) => {
      this.isochronesOptions_.method = e.target.value;
    });
    this.jqElement_.find('input[name=inverse]').change((e) => {
      this.isochronesOptions_.inverse = parseInt(e.target.value, 10);
    });

    this.jqElement_.find('button.calculate').click(() => this.emit(EventType.SUBMIT, this.isochronesOptions_)); // Calculate
    this.jqElement_.find('button.save').click(() => this.emit(EventType.SAVE_KML)); // Save
    this.jqElement_.find('button.remove').click(() => this.emit(EventType.REMOVE)); // Remove

    this.mdcDistSliderCar_ = new MDCSlider(jqDistsSliderCar.get(0));
    this.mdcDistSliderBicycle_ = new MDCSlider(jqDistsSliderBicycle.get(0));
    this.mdcDistSliderOnFoot_ = new MDCSlider(jqDistsSliderOnFoot.get(0));
    this.mdcTimeSlider_ = new MDCSlider(jqTimesSlider.get(0));
    this.mdcDistSliderCar_.listen('MDCSlider:change', () => {
      this.updateDistanceSliders(this.mdcDistSliderCar_.getValue(), Net.CAR);
    });
    this.mdcDistSliderBicycle_.listen('MDCSlider:change', () => {
      this.updateDistanceSliders(this.mdcDistSliderBicycle_.getValue(), Net.BIKE);
    });
    this.mdcDistSliderOnFoot_.listen('MDCSlider:change', () => {
      this.updateDistanceSliders(this.mdcDistSliderOnFoot_.getValue(), Net.FOOT);
    });
    this.mdcTimeSlider_.listen('MDCSlider:change', () => {
      this.isochronesOptions_.isolevels = this.mdcTimeSlider_.getValue() * 60 * 1000; // in milliseconds
      this.jqElement_.find('.sliders .timeslider .value').text(`${this.mdcTimeSlider_.getValue()}min`);
    });
    this.jqElement_.find('input[name=increment]').change((e) => {
      this.isochronesOptions_.increment = e.target.checked;
      if (e.target.checked) {
        this.mdcTimeSlider_.setValue(15);
        this.isochronesOptions_.isolevels = 15 * 60 * 1000;
        // this.mdcTimeSlider_.root.setAttribute('aria-valuemax', '30');
        // this.jqElement_.find('.timeslider .slider__thumb ').attr('aria-valuemax', '30');
        this.mdcTimeSlider_.setDisabled(true);
        const span = this.jqElement_.find('.time .mdc-slider__thumb span.value');
        span.text('15min');
      } else {
        this.mdcTimeSlider_.setDisabled(false);
      }
    });

    new MDCLinearProgress(this.jqElement_.find('.progressbar').get(0)); // eslint-disable-line no-new
  }

  /**
   * @param {boolean} show Show
   */
  setLoading(show) {
    const jqLoading = this.jqElement_.find('.progressbar');
    if (show) {
      jqLoading.show();
    } else {
      jqLoading.hide();
    }
  }

  /**
   * @param {boolean} drawn Drawn
   */
  setDrawn(drawn) {
    const jqSaveBtn = this.jqElement_.find('button.save');
    const jqRemoveBtn = this.jqElement_.find('button.remove');
    if (drawn) {
      jqSaveBtn.show();
      jqRemoveBtn.show();
    } else {
      jqSaveBtn.hide();
      jqRemoveBtn.hide();
    }
  }

  /**
   * @private
   */
  updateDistanceSliderVisibility() {
    if (this.isochronesOptions_.net === Net.CAR) {
      this.jqElement_.find('#methodRow').show();
      this.jqElement_.find('.sliders .distbicycle').hide();
      this.jqElement_.find('.sliders .distonfoot').hide();
      this.jqElement_.find('.sliders .distcar').show();
    } else if (this.isochronesOptions_.net === Net.BIKE) {
      this.jqElement_.find('#methodRow').hide();
      this.jqElement_.find('.sliders .distcar').hide();
      this.jqElement_.find('.sliders .distonfoot').hide();
      this.jqElement_.find('.sliders .distbicycle').show();
    } else {
      this.jqElement_.find('#methodRow').hide();
      this.jqElement_.find('.sliders .distcar').hide();
      this.jqElement_.find('.sliders .distbicycle').hide();
      this.jqElement_.find('.sliders .distonfoot').show();
    }
  }

  /**
   * @private
   * @param {number} value newValue
   * @param {string} net `car|bike|foot`
   */
  updateDistanceSliders(value, net) {
    const bicycleValue = value > DistanceMaxValue.BIKE ? DistanceMaxValue.BIKE : value;
    const onFootValue = value > DistanceMaxValue.FOOT ? DistanceMaxValue.FOOT : value;

    if (net !== Net.CAR) {
      this.mdcDistSliderCar_.setValue(value);
    } else {
      this.isochronesOptions_.isolevels = value * 1000;
      this.jqElement_.find('.sliders .distslidercar .value').text(`${value}km`);
    }

    if (net !== Net.BIKE) {
      this.mdcDistSliderBicycle_.setValue(bicycleValue);
    } else {
      this.isochronesOptions_.isolevels = bicycleValue * 1000;
      this.jqElement_.find('.sliders .distsliderbicycle .value').text(`${bicycleValue}km`);
    }

    if (net !== Net.FOOT) {
      this.mdcDistSliderOnFoot_.setValue(onFootValue);
    } else {
      this.isochronesOptions_.isolevels = onFootValue * 1000;
      this.jqElement_.find('.sliders .distslideronfoot .value').text(`${onFootValue}km`);
    }
  }
}

export default VIsochrones;
